import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_ROOT as API } from './config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Activity tracking for auto-logout
  const updateActivity = () => {
    setLastActivity(Date.now());
  };

  // Load token from localStorage on app start
  useEffect(() => {
    const savedToken = localStorage.getItem('fastplanet_token');
    const savedUser = localStorage.getItem('fastplanet_user');
    const tokenExpiry = localStorage.getItem('fastplanet_token_expiry');
    
    if (savedToken && savedUser && tokenExpiry) {
      const now = Date.now();
      if (now < parseInt(tokenExpiry)) {
        // Set up axios default headers first
        axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
        setToken(savedToken);
        // Use cached user immediately while syncing from server
        setUser(JSON.parse(savedUser));
        // Sync fresh user data from server
        axios.get(`${API}/auth/me`)
          .then(response => {
            const freshUser = response.data;
            localStorage.setItem('fastplanet_user', JSON.stringify(freshUser));
            setUser(freshUser);
          })
          .catch(() => {
            // If /me fails (e.g. token revoked), logout
            logout();
          })
          .finally(() => {
            setLoading(false);
          });
        return;
      } else {
        // Token expired, clear storage
        logout();
      }
    }
    setLoading(false);
  }, []);

  // Auto-logout timer based on user's session duration
  useEffect(() => {
    if (!user || !token) return;

    const sessionDuration = (user.session_duration_minutes || 60) * 60 * 1000; // Convert to milliseconds
    
    const checkActivity = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivity;
      
      if (timeSinceActivity > sessionDuration) {
        logout('Session abgelaufen aufgrund Inaktivität');
      }
    };

    const activityTimer = setInterval(checkActivity, 60000); // Check every minute

    return () => clearInterval(activityTimer);
  }, [user, token, lastActivity]);

  // Track user activity
  useEffect(() => {
    if (!user) return;

    const activities = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      updateActivity();
    };

    activities.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      activities.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [user]);

  const login = async (credentials) => {
    try {
      const response = await axios.post(`${API}/auth/login`, credentials);
      const { accessToken, user: userData, expiresIn } = response.data;

      const expiryTime = Date.now() + (expiresIn * 1000);

      // Store in localStorage
      localStorage.setItem('fastplanet_token', accessToken);
      localStorage.setItem('fastplanet_user', JSON.stringify(userData));
      localStorage.setItem('fastplanet_token_expiry', expiryTime.toString());

      // Set state
      setToken(accessToken);
      setUser(userData);
      setLastActivity(Date.now());

      // Set up axios default headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      // Show success message with draft restoration info
      const savedView = localStorage.getItem('fastplanet_last_view');
      const savedDraft = localStorage.getItem('fastplanet_building_draft');

      if (savedView && savedView !== 'dashboard') {
        setTimeout(() => {
          let message = '✅ Erfolgreich angemeldet!\n\n';
          message += `Sie werden zu Ihrer letzten Ansicht weitergeleitet: ${savedView === 'buildings' ? 'Gebäude-Übersicht' : savedView === 'building-form' ? 'Gebäudeerfassung' : 'Building-Detail'}\n\n`;

          if (savedDraft && savedView === 'building-form') {
            const draft = JSON.parse(savedDraft);
            const savedTime = new Date(draft.savedAt).toLocaleString('de-DE');
            message += `💾 Entwurf gefunden vom ${savedTime}\nIhre unvollständigen Formulardaten werden wiederhergestellt.`;
          }

          alert(message);
        }, 1000);
      }

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Login fehlgeschlagen';
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const { accessToken, user: newUser, expiresIn } = response.data;

      const expiryTime = Date.now() + (expiresIn * 1000);

      // Store in localStorage
      localStorage.setItem('fastplanet_token', accessToken);
      localStorage.setItem('fastplanet_user', JSON.stringify(newUser));
      localStorage.setItem('fastplanet_token_expiry', expiryTime.toString());

      // Set state
      setToken(accessToken);
      setUser(newUser);
      setLastActivity(Date.now());

      // Set up axios default headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Registrierung fehlgeschlagen';
      return { success: false, error: errorMessage };
    }
  };

  const logout = (reason = '') => {
    // Clear state
    setToken(null);
    setUser(null);
    
    // Clear all session data
    localStorage.removeItem('fastplanet_token');
    localStorage.removeItem('fastplanet_user');
    localStorage.removeItem('fastplanet_token_expiry');
    localStorage.removeItem('fastplanet_last_view');
    localStorage.removeItem('fastplanet_building_draft');
    localStorage.removeItem('fastplanet_selected_building');
    
    // Clear axios headers
    delete axios.defaults.headers.common['Authorization'];
    
    console.log('User logged out and all session data cleared');
    
    if (reason) {
      alert(reason);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put(`${API}/auth/me/profile`, profileData);
      const updatedUser = response.data;
      
      localStorage.setItem('fastplanet_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Profil-Update fehlgeschlagen';
      return { success: false, error: errorMessage };
    }
  };

  const resetPassword = async (resetData) => {
    // TODO: Backend endpoint POST /api/auth/reset-password not yet implemented
    return { success: false, error: 'Funktion noch nicht verfügbar' };
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateProfile,
    resetPassword,
    updateActivity
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};